import type React from 'react';
import { useTranslation } from 'react-i18next';
import { EmployeeFormDrawer } from '@/pages/accounting/registers/salaries/components';
import { resolveLocale } from '@/pages/accounting/statements/statementMeta';
import { resolveLabel } from '@/pages/accounting/statements/statementMeta';
import { useCurrentSalaryRates } from '@/pages/accounting/registers/salaries/useSalaryRates';
import { PATH } from '@/router/PATH';
import type { ActionSurfaceProps } from '../../controlCenterSurfaces';

/**
 * "Add employee", opened from the launcher — the Salaries register's **own**
 * drawer, not a second employee form.
 *
 * This is the whole point of the surface registry. The drawer already owns its
 * create mutation and its cache invalidation, so mounting it here is enough:
 * the employee lands on the register, the register's queries are invalidated,
 * and following the link in the confirmation shows the new row. Nothing about
 * the write is reimplemented on this page, and there is one employee form in
 * the product to keep correct.
 *
 * The adapter exists so the Control Center's View never imports salaries
 * internals. Everything feature-shaped is here: which component, its props, and
 * how a saved `Employee` becomes a sentence and a destination.
 *
 * It serves itself the tax rates rather than taking them as a prop — that query
 * is shared and cached (five-minute `staleTime`), and needing to know about
 * pension percentages in order to open a launcher is exactly the coupling this
 * seam exists to avoid.
 */
export const AddEmployeeSurface: React.FC<ActionSurfaceProps> = ({
    onClose,
    onDone,
}) => {
    const { i18n } = useTranslation('pages');
    const locale = resolveLocale(i18n.language);
    const rates = useCurrentSalaryRates();

    return (
        <EmployeeFormDrawer
            // Always a create: the launcher hires, the register edits.
            employee={null}
            isOpen
            onClose={onClose}
            onSaved={(employee, meta) =>
                onDone({
                    messageKey: meta.alreadyExisted
                        ? 'controlCenter.outcome.employeeAlreadyExisted'
                        : 'controlCenter.outcome.employeeAdded',
                    messageParams: {
                        name: resolveLabel(employee.name, locale, employee.id),
                    },
                    to: PATH.ACCOUNTING.REGISTERS.SALARIES,
                    linkKey: 'controlCenter.outcome.openSalaries',
                })
            }
            rates={rates}
        />
    );
};
